"use client";

import React, { useState } from "react";
import {
  Shield,
  CheckCircle2,
  Calculator,
  ArrowRight,
  TrendingUp,
  Award,
  Zap,
  Lock,
} from "lucide-react";
import { INSURANCE_PRODUCTS, calculateIndicativePremium } from "@/lib/demos/insuranceData";

interface InsuranceHeroProps {
  currency: "INR" | "USD";
  onOpenProposalModal: (initialData?: {
    productId: string;
    productName: string;
    sumAssured: number;
    annualPremium: number;
    age: number;
    isTobaccoUser: boolean;
  }) => void;
}

export default function InsuranceHero({
  currency,
  onOpenProposalModal,
}: InsuranceHeroProps) {
  const [selectedProductId, setSelectedProductId] = useState("aegis-term-shield");
  const [age, setAge] = useState(32);
  const [isTobaccoUser, setIsTobaccoUser] = useState(false);

  // Sum assured defaults based on currency & product
  const [sumAssuredINR, setSumAssuredINR] = useState(20000000); // 2 Cr
  const [sumAssuredUSD, setSumAssuredUSD] = useState(250000); // $250k

  const activeSumAssured = currency === "USD" ? sumAssuredUSD : sumAssuredINR;

  const currentProduct =
    INSURANCE_PRODUCTS.find((p) => p.id === selectedProductId) || INSURANCE_PRODUCTS[0];

  // Calculate live premium
  const calculatedPremium = calculateIndicativePremium({
    productId: selectedProductId,
    age,
    sumAssured: activeSumAssured,
    isTobaccoUser,
    tenureYears: 30,
    currency,
  });

  // Calculate indicative tax savings (approx 30% slab)
  const estimatedTaxSaving = Math.round(calculatedPremium * 0.3);

  const sym = currency === "USD" ? "$" : "₹";

  const handleOpenProposal = () => {
    onOpenProposalModal({
      productId: currentProduct.id,
      productName: currentProduct.name,
      sumAssured: activeSumAssured,
      annualPremium: calculatedPremium,
      age,
      isTobaccoUser,
    });
  };

  return (
    <section className="relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28 bg-gradient-to-b from-slate-950 via-[#060d1f] to-slate-950">
      {/* Subtle background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-blue-600/10 blur-[130px] pointer-events-none rounded-full" />
      <div className="absolute top-10 right-10 w-96 h-96 bg-indigo-500/5 blur-[100px] pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Branding & Pillars */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-950/80 border border-blue-800/60 text-blue-300 text-xs font-semibold shadow-inner">
              <Shield className="w-3.5 h-3.5 text-blue-400" />
              <span>IRDAI Reg. 892/2019 • AAA Rated Reinsurance Security</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.15]">
              Institutional Wealth Protection &amp;{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-cyan-300">
                High-Yield Life Coverage
              </span>
            </h1>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Experience the future of insurtech: instant actuarial underwriting, paperless UIDAI
              e-KYC verification, 100% sponsored executive diagnostic profiles, and automated
              IRDAI-compliant policy issuance with official Section 80C &amp; 80D tax exemption certificates.
            </p>

            {/* 4 Trust Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                <div className="text-lg font-bold text-blue-400">99.2%</div>
                <div className="text-[11px] text-slate-400 font-medium">Claim Ratio</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                <div className="text-lg font-bold text-emerald-400">15 Mins</div>
                <div className="text-[11px] text-slate-400 font-medium">Paperless e-KYC</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                <div className="text-lg font-bold text-indigo-400">Zero Cost</div>
                <div className="text-[11px] text-slate-400 font-medium">Medical Labs</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                <div className="text-lg font-bold text-amber-400">80C / 80D</div>
                <div className="text-[11px] text-slate-400 font-medium">Tax Benefit PDF</div>
              </div>
            </div>

            {/* CRM Bridge Banner */}
            <div className="p-4 rounded-xl bg-blue-950/40 border border-blue-900/50 flex items-start gap-3.5">
              <div className="p-2 rounded-lg bg-blue-600 text-white shrink-0">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div className="text-xs">
                <p className="font-semibold text-white">Live Real-Time Underwriting Sync</p>
                <p className="text-slate-300 mt-0.5">
                  Submitting a proposal instantly streams into the 6-stage Underwriting CRM, triggering automated medical passes and tax invoice certificates via Hostinger SMTP.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Live Premium Calculator */}
          <div className="lg:col-span-6">
            <div className="bg-slate-900/95 border border-blue-900/50 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-blue-950/80 relative backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                    <Calculator className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white tracking-wide">
                      Instant Premium Calculator
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Real-time actuarial estimation engine
                    </p>
                  </div>
                </div>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-950/80 text-emerald-300 border border-emerald-800/60">
                  Instant Quote
                </span>
              </div>

              {/* Product Selector Pills */}
              <div className="space-y-2 mb-5">
                <label className="text-xs font-semibold text-slate-300 block">
                  Select Insurance or Wealth Plan:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {INSURANCE_PRODUCTS.map((prod) => (
                    <button
                      key={prod.id}
                      type="button"
                      onClick={() => {
                        setSelectedProductId(prod.id);
                        if (currency === "USD") {
                          setSumAssuredUSD(prod.defaultSumAssuredUSD);
                        } else {
                          setSumAssuredINR(prod.defaultSumAssuredINR);
                        }
                      }}
                      className={`p-2.5 rounded-xl text-left text-xs transition-all border ${
                        selectedProductId === prod.id
                          ? "bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-950"
                          : "bg-slate-950/80 text-slate-400 border-slate-800 hover:border-slate-700 hover:text-slate-200"
                      }`}
                    >
                      <div className="font-bold truncate">{prod.name.split(" ")[0]} {prod.name.split(" ")[1]}</div>
                      <div className="text-[10px] opacity-80 mt-0.5">{prod.category}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Slider */}
              <div className="space-y-2 mb-5">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-slate-300">Applicant Age:</span>
                  <span className="font-bold text-blue-400 text-sm">{age} Years</span>
                </div>
                <input
                  type="range"
                  min={18}
                  max={65}
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                  <span>18 Yrs</span>
                  <span>40 Yrs</span>
                  <span>65 Yrs</span>
                </div>
              </div>

              {/* Sum Assured Selector */}
              <div className="space-y-2 mb-5">
                <label className="text-xs font-semibold text-slate-300 block">
                  Sum Assured (Life / Health Protection):
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {currency === "USD" ? (
                    <>
                      {[100000, 250000, 500000].map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setSumAssuredUSD(amt)}
                          className={`py-2 rounded-lg text-xs font-semibold border transition-all ${
                            sumAssuredUSD === amt
                              ? "bg-blue-950 border-blue-500 text-blue-300"
                              : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700"
                          }`}
                        >
                          ${(amt / 1000).toFixed(0)}k Cover
                        </button>
                      ))}
                    </>
                  ) : (
                    <>
                      {[10000000, 20000000, 30000000].map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setSumAssuredINR(amt)}
                          className={`py-2 rounded-lg text-xs font-semibold border transition-all ${
                            sumAssuredINR === amt
                              ? "bg-blue-950 border-blue-500 text-blue-300"
                              : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700"
                          }`}
                        >
                          ₹{(amt / 10000000).toFixed(0)} Crore
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Tobacco Smoker Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/80 border border-slate-800 mb-6">
                <div>
                  <div className="text-xs font-semibold text-slate-200">Tobacco / Nicotine Consumer?</div>
                  <div className="text-[10px] text-slate-400">Non-smokers enjoy up to 35% discounted actuarial rates</div>
                </div>
                <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setIsTobaccoUser(false)}
                    className={`px-2.5 py-1 rounded font-semibold transition-colors ${
                      !isTobaccoUser ? "bg-emerald-600 text-white" : "text-slate-400"
                    }`}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsTobaccoUser(true)}
                    className={`px-2.5 py-1 rounded font-semibold transition-colors ${
                      isTobaccoUser ? "bg-amber-600 text-white" : "text-slate-400"
                    }`}
                  >
                    Yes
                  </button>
                </div>
              </div>

              {/* Calculated Result Box */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-950/90 to-slate-900 border border-blue-800/80 mb-6">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[11px] text-blue-300 font-semibold uppercase tracking-wider">
                      Indicative Annual Premium
                    </div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-white mt-0.5">
                      {sym}{calculatedPremium.toLocaleString()}{" "}
                      <span className="text-xs font-normal text-slate-400">/ year</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider">
                      {currentProduct.taxBenefit} Savings
                    </div>
                    <div className="text-sm font-bold text-emerald-300">
                      Save ~{sym}{estimatedTaxSaving.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA to lock quote & open proposal */}
              <button
                type="button"
                onClick={handleOpenProposal}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-bold shadow-lg shadow-blue-900/50 hover:shadow-blue-800/60 transition-all cursor-pointer"
              >
                <span>Lock Indicative Quote &amp; Begin e-KYC</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <div className="flex items-center justify-center gap-4 text-[10px] text-slate-400 mt-4">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  Instant Proposal PDF
                </span>
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3 text-blue-400" />
                  256-bit AES e-KYC
                </span>
                <span className="flex items-center gap-1">
                  <Award className="w-3 h-3 text-amber-400" />
                  Section 80C Benefit
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
